import { baseApiService } from './base'
import type { FetchOptions } from './base'
import { uploaderService } from '../../services/uploader'
import { uploadFilesToS3 } from './files'
import type { S3UploadResult } from './files'
import { getAwsS3Config } from './api.settings'

// Re-export for external use
export { getAwsS3Config }

// FileWithMetadata type for handling files with metadata
export interface FileWithMetadata {
  file: File
  [key: string]: any
}

export interface CRUser {
  id: number
  name: string
  email: string
}

export interface MigrationParams {
  parentId: number
  description?: string
  documentTypeTemplateId?: number
  createCR?: boolean
  isCertifiedCopy?: boolean
  isTriggerChangeBlindingStatus?: boolean
  continueToEditPhase?: boolean
  folderTypeTemplateId?: number
  expirationDate?: string
  combineImages?: boolean
  crUsers?: {
    approvers?: CRUser[]
    collaborators?: CRUser[]
    reviewers?: CRUser[]
  }
}

export interface ESignatureValidation {
  // DSO (username/password) authentication
  username?: string
  password?: string
  // SSO/OKTA (token-based) authentication
  relayState?: string
  result?: any
}

export interface MigrationDetails extends Omit<MigrationParams, 'crUsers' | 'createCR' | 'parentId'> {
  parent: number
  esignature?: string
  esignatureCC?: string
  approvers?: string
  collaborators?: string
  reviewers?: string
  proceedToEditPhase?: boolean
  isBlinded?: string
}

export interface MigrationResult {
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

export async function uploadFilesDirect(
  files: File[] | any[],
  migrationDetails: MigrationDetails,
  onProgress?: (percentage: number) => void,
  abortSignal?: AbortSignal,
  uploadId?: number
): Promise<MigrationResult> {
  console.log('[uploadFilesDirect] Called with', files.length, 'files')
  console.log(
    '[uploadFilesDirect] Files:',
    files.map((f: any) => f.name || f.file?.name)
  )
  console.log('[uploadFilesDirect] Migration details:', migrationDetails)

  try {
    const formData = new FormData()
    let apiUrl: string | null = null
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '0.0.0.0' ||
        import.meta.env.DEV)
    ) {
      // Local dev: use Vite env or fallback
      apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5173/noApiDefined'
      console.log('[uploadFilesDirect] Using dev API URL:', apiUrl)
    } else {
      // Production: use attribute from DOM
      apiUrl = document.documentElement.getAttribute('api-url')
      console.log('[uploadFilesDirect] Using production API URL from DOM:', apiUrl)
    }
    if (!apiUrl) {
      console.error('[uploadFilesDirect] api-url attribute not found!')
      throw new Error('api-url attribute not found for Direct RCM validation')
    }

    // Extract actual File objects if FileWithMetadata objects are passed
    const actualFiles = files.map((file) => {
      // Check if it's a FileWithMetadata object with a 'file' property
      if (file && typeof file === 'object' && 'file' in file && file.file instanceof File) {
        console.log('[uploadFilesDirect] Extracting File from FileWithMetadata:', file.file.name)
        return file.file
      }
      // Otherwise assume it's already a File object
      console.log('[uploadFilesDirect] Using file directly:', file.name)
      return file
    })

    console.log(
      '[uploadFilesDirect] Actual files to upload:',
      actualFiles.length,
      actualFiles.map((f) => f.name)
    )

    // Append files
    actualFiles.forEach((file) => {
      console.log('[uploadFilesDirect] Appending file to FormData:', file.name, file.size, 'bytes')
      formData.append('data', file)
    })

    // Append documentTypeTemplateId per file if present
    if (migrationDetails.documentTypeTemplateId) {
      actualFiles.forEach(() => {
        formData.append('documentTypeTemplateId', String(migrationDetails.documentTypeTemplateId))
      })
    }

    // Append all other migration details (excluding documentTypeTemplateId as it's handled above)
    const detailsToAppend = { ...migrationDetails }
    delete detailsToAppend.documentTypeTemplateId

    Object.entries(detailsToAppend).forEach(([key, value]) => {
      // Special handling for esignature - always append even if 'null' string (matching legacy behavior)
      if (key === 'esignature' && value === 'null') {
        formData.append(key, 'null')
      }
      // Only append other values if they are not undefined, not null, and not an empty string
      else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value))
      }
    })

    const options: FetchOptions = {
      method: 'POST',
      body: formData,
      signal: abortSignal,
      baseUrl: apiUrl,
      disableCSelections: true
    }

    // Track upload progress if uploadId provided
    if (uploadId !== undefined && onProgress) {
      // Note: For now, progress tracking with baseApiService.fetch is limited
      // Full progress support will require XMLHttpRequest implementation
      // The uploadId is tracked but progress callbacks may not fire during upload
      console.warn('Upload progress tracking is limited with current fetch implementation')
    }

    console.log('[uploadFilesDirect] Making API call to /api/files/ with baseUrl:', apiUrl)
    console.log(
      '[uploadFilesDirect] FormData entries:',
      Array.from(formData.entries()).map(([k, v]) => k + '=' + (v instanceof File ? v.name : v))
    )

    // Note: Progress tracking will be implemented in Phase 2 with UploadManager
    const result = await baseApiService.fetch<Record<string, unknown>>('/api/files/', options)
    console.log('[uploadFilesDirect] API call completed, result:', result)

    // Mark upload as complete if uploadId provided
    if (uploadId !== undefined) {
      uploaderService.setProgress(uploadId, 100)
    }

    return {
      success: true,
      data: result
    }
  } catch (error) {
    console.error('[uploadFilesDirect] Upload failed with error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    }
  }
}

export function configureMigrationDetails(
  params: MigrationParams,
  validation?: ESignatureValidation
): MigrationDetails {
  const migrationDetails: MigrationDetails = {
    parent: params.parentId,
    description: params.description,
    documentTypeTemplateId: params.documentTypeTemplateId,
    // Always set esignature (matching legacy behavior - it's always present, either as JSON or null)
    esignature: validation ? JSON.stringify(validation) : 'null',
    approvers: params.crUsers?.approvers ? JSON.stringify(params.crUsers.approvers) : undefined,
    collaborators: params.crUsers?.collaborators ? JSON.stringify(params.crUsers.collaborators) : undefined,
    reviewers: params.crUsers?.reviewers ? JSON.stringify(params.crUsers.reviewers) : undefined
  }

  // Add optional parameters
  if (params.continueToEditPhase) {
    migrationDetails.proceedToEditPhase = params.continueToEditPhase
  }
  if (params.folderTypeTemplateId) {
    migrationDetails.folderTypeTemplateId = params.folderTypeTemplateId
  }
  if (params.isCertifiedCopy) {
    migrationDetails.esignatureCC = validation ? JSON.stringify(validation) : undefined
  }
  if (params.isTriggerChangeBlindingStatus) {
    migrationDetails.isBlinded = 'false'
  }
  if (params.expirationDate) {
    migrationDetails.expirationDate = params.expirationDate
  }
  if (params.combineImages) {
    migrationDetails.combineImages = params.combineImages
  }

  return migrationDetails
}

export async function submitS3Upload(
  s3ObjectName: string,
  migrationDetails: MigrationDetails
): Promise<MigrationResult> {
  console.log('[submitS3Upload] Called with s3ObjectName:', s3ObjectName)
  console.log('[submitS3Upload] Migration details:', migrationDetails)

  try {
    const formData = new FormData()
    formData.append('s3ObjectName', s3ObjectName)

    Object.entries(migrationDetails).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })

    let apiUrl: string | null = null
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '0.0.0.0' ||
        import.meta.env.DEV)
    ) {
      // Local dev: use Vite env or fallback
      apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5173/noApiDefined'
    } else {
      // Production: use attribute from DOM
      apiUrl = document.documentElement.getAttribute('api-url')
    }

    console.log('[submitS3Upload] Using API URL:', apiUrl)

    if (!apiUrl) {
      throw new Error('api-url attribute not found for Direct RCM validation')
    }

    console.log(
      '[submitS3Upload] FormData entries:',
      Array.from(formData.entries()).map(([k, v]) => `${k}=${v}`)
    )

    const options: FetchOptions = {
      method: 'POST',
      body: formData,
      baseUrl: apiUrl,
      disableCSelections: true,
      directConnection: true,
      headers: {
        // Don't set Content-Type for FormData
      }
    }

    console.log('[submitS3Upload] Making API call to /api/files/s3Upload...')
    const result = await baseApiService.fetch<Record<string, unknown>>('/api/files/s3Upload', options)
    console.log('[submitS3Upload] API call completed successfully, result:', result)

    return {
      success: true,
      data: result
    }
  } catch (error) {
    console.error('[submitS3Upload] Failed with error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'S3 submission failed'
    }
  }
}

export async function performMigration(
  files: File[] | FileWithMetadata[],
  params: MigrationParams,
  validation?: ESignatureValidation,
  onProgress?: (percentage: number) => void,
  abortSignal?: AbortSignal,
  uploadId?: number
): Promise<MigrationResult> {
  console.log('[performMigration] Starting with files:', files.length, 'files')
  console.log(
    '[performMigration] Files:',
    files.map((f: any) => f.name || f.file?.name)
  )
  console.log('[performMigration] Params:', params)

  const migrationDetails = configureMigrationDetails(params, validation)
  console.log('[performMigration] Migration details:', migrationDetails)

  // Extract actual File objects if FileWithMetadata objects are passed
  const actualFiles = files.map((file) => {
    if (file && typeof file === 'object' && 'file' in file && file.file instanceof File) {
      console.log('[performMigration] Extracting File from FileWithMetadata:', (file as any).file.name)
      return file.file
    }
    console.log('[performMigration] Using file directly:', (file as File).name)
    return file as File
  })

  console.log(
    '[performMigration] Actual files to upload:',
    actualFiles.length,
    actualFiles.map((f) => f.name)
  )

  try {
    // Check if AWS S3 upload is enabled
    console.log('[performMigration] Fetching AWS S3 config...')
    const awsConfig = (await getAwsS3Config()) as any
    console.log('[performMigration] AWS config received:', awsConfig)

    if (awsConfig.uploadEnabled) {
      console.log('[performMigration] Using S3 upload path')
      // Wrap onProgress to update uploader widget if uploadId is provided
      const wrappedProgress =
        uploadId !== undefined
          ? (percentage: number) => {
              // Update uploader widget progress
              uploaderService.setProgress(uploadId, percentage)
              // Call original progress callback
              onProgress?.(percentage)
            }
          : onProgress

      // Use S3 upload path
      const s3Result = await uploadFilesToS3(actualFiles, {
        onProgress: wrappedProgress,
        abortSignal
      })
      console.log('[performMigration] S3 upload result:', s3Result)

      // Check if S3 upload succeeded
      if (!s3Result.success || !s3Result.uuid) {
        console.error('[performMigration] S3 upload failed:', s3Result.error)
        return {
          success: false,
          error: s3Result.error || 'S3 upload failed'
        }
      }

      // Submit the S3 upload to complete migration
      console.log('[performMigration] Submitting S3 upload with UUID:', s3Result.uuid)
      return await submitS3Upload(s3Result.uuid, migrationDetails)
    } else {
      console.log('[performMigration] Using direct upload path')
      // Use direct upload path
      const result = await uploadFilesDirect(actualFiles, migrationDetails, onProgress, abortSignal, uploadId)
      console.log('[performMigration] Direct upload result:', result)
      return result
    }
  } catch (error) {
    console.error('[performMigration] Error during upload:', error)
    // If AWS config fetch fails, fallback to direct upload (matching legacy behavior)
    try {
      console.log('[performMigration] Falling back to direct upload')
      const result = await uploadFilesDirect(actualFiles, migrationDetails, onProgress, abortSignal, uploadId)
      console.log('[performMigration] Fallback direct upload result:', result)
      return result
    } catch (fallbackError) {
      console.error('[performMigration] Fallback upload also failed:', fallbackError)
      return {
        success: false,
        error: fallbackError instanceof Error ? fallbackError.message : 'Migration failed'
      }
    }
  }
}

/**
 * Main migration function that handles file migration with optional e-signature
 * This is the primary entry point for document migration functionality
 *
 * @param files - Array of files to migrate
 * @param params - Migration parameters (parent folder, doc type, workflow settings, etc.)
 * @param onProgress - Optional callback for upload progress (0-100)
 * @param onSuccess - Optional callback on successful migration
 * @param onError - Optional callback on migration error
 * @param abortSignal - Optional AbortSignal to cancel the migration
 * @param requireESignature - Whether e-signature is required (based on Auth.requireESignatureForFileCommands())
 * @param eSignatureValidation - E-signature credentials if required
 * @returns Promise<MigrationResult>
 *
 * @example
 * ```typescript
 * const result = await doMigration(
 *   files,
 *   { parentId: 123, documentTypeTemplateId: 456, createCR: true },
 *   (progress) => console.log(`${progress}% complete`),
 *   (data) => console.log('Success!', data),
 *   (error) => console.error('Failed:', error)
 * )
 * ```
 */
export async function doMigration(
  files: File[] | FileWithMetadata[],
  params: MigrationParams,
  onProgress?: (percentage: number) => void,
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
  abortSignal?: AbortSignal,
  requireESignature?: boolean,
  eSignatureValidation?: ESignatureValidation
) {
  console.log('[doMigration] Called with', files.length, 'files')
  console.log(
    '[doMigration] Files:',
    files.map((f: any) => f.name || f.file?.name)
  )
  console.log('[doMigration] Params:', params)
  console.log('[doMigration] requireESignature:', requireESignature)
  console.log('[doMigration] eSignatureValidation:', eSignatureValidation ? 'provided' : 'not provided')

  try {
    // If e-signature is required but not provided, throw error
    // In the legacy app, this would trigger Modal.password()
    // In the new app, the calling component should show the e-signature modal first
    if (requireESignature && !eSignatureValidation) {
      const error = 'E-signature required but not provided'
      console.error('[doMigration] E-signature required but not provided')
      if (onError) {
        onError(error)
      }
      return {
        success: false,
        error
      }
    }

    // Create abort controller for uploader service
    const abortController = {
      resolve: () => {
        if (abortSignal) {
          // Trigger abort on the signal
          const controller = (abortSignal as any).__controller
          if (controller && typeof controller.abort === 'function') {
            controller.abort()
          }
        }
      }
    }

    // Extract actual File objects for uploader service
    const fileObjects = files.map((f) => (f as any).file || f) as File[]
    console.log(
      '[doMigration] File objects for uploader:',
      fileObjects.length,
      fileObjects.map((f) => f.name)
    )

    console.log('[doMigration] Creating migration promise...')
    // Start the uploader first to get uploadId, then create the migration promise
    let resolveUploadDef: (value: MigrationResult) => void
    let rejectUploadDef: (reason: any) => void

    const migrationPromise = new Promise<MigrationResult>((resolve, reject) => {
      resolveUploadDef = resolve
      rejectUploadDef = reject
    })

    const uploadId = uploaderService.startUpload(fileObjects, abortController, {
      promise: migrationPromise
    })

    // Now perform migration with uploadId available for progress tracking
    try {
      const result = await performMigration(
        files,
        params,
        eSignatureValidation,
        onProgress,
        abortSignal,
        uploadId // Pass uploadId so progress updates can reach the uploader widget
      )

      resolveUploadDef!(result)

      // Call success/error callbacks
      if (result.success && onSuccess) {
        onSuccess(result.data)
      } else if (!result.success && onError) {
        onError(result.error)
      }

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Migration failed'

      rejectUploadDef!(error)

      if (onError) {
        onError(errorMessage)
      }

      return {
        success: false,
        error: errorMessage
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Migration setup failed'
    console.error('[doMigration] Migration setup failed with error:', errorMessage)
  }
}

/**
 * Upload and file migration for ETMF filing workflow
 * Used specifically for site upload scenarios
 *
 * @param files - Files to upload
 * @param configObj - Configuration object with parentId, isCertifiedCopy, isTriggerChangeBlindingStatus
 * @param onProgress - Progress callback
 * @param abortSignal - Abort signal
 * @param eSignatureValidation - E-signature if certified copy
 * @returns Promise<MigrationResult>
 */
export async function doMigrationForFiling(
  files: File[] | FileWithMetadata[],
  configObj: {
    parentId: number
    isCertifiedCopy?: boolean
    isTriggerChangeBlindingStatus?: boolean
  },
  onProgress?: (percentage: number) => void,
  abortSignal?: AbortSignal,
  eSignatureValidation?: ESignatureValidation
): Promise<MigrationResult> {
  try {
    // Create abort controller for uploader service
    const abortController = {
      resolve: () => {
        if (abortSignal) {
          const controller = (abortSignal as any).__controller
          if (controller && typeof controller.abort === 'function') {
            controller.abort()
          }
        }
      }
    }

    // Configure migration details for upload and file
    const migrationDetails: any = {
      parent: configObj.parentId
    }

    if (configObj.isCertifiedCopy) {
      migrationDetails.esignatureCC = eSignatureValidation ? JSON.stringify(eSignatureValidation) : null
    }

    if (configObj.isTriggerChangeBlindingStatus) {
      migrationDetails.isBlinded = 'false'
    }

    // Upload files for filing (ETMF specific endpoint)
    const formData = new FormData()

    files.forEach((file) => {
      // Extract actual File object if FileWithMetadata
      const actualFile = (file as any).file || file
      formData.append('data', actualFile)
    })

    Object.entries(migrationDetails).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })

    const apiUrl = document.documentElement.getAttribute('api-url')
    if (!apiUrl) {
      throw new Error('api-url attribute not found for Direct RCM validation')
    }

    const options: FetchOptions = {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData
      },
      signal: abortSignal,
      baseUrl: apiUrl,
      disableCSelections: true
    }

    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '0.0.0.0' ||
        import.meta.env.DEV)
    ) {
      // Local dev: use Vite env or fallback
      options.baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5173/noApiDefined'
    } else {
      // Production: use attribute from DOM
      options.baseUrl = document.documentElement.getAttribute('api-url') ?? ''
    }

    // Create upload promise
    const uploadPromise = baseApiService.fetch<Record<string, unknown>>('/api/v1/etmf/uploadfiles', options)

    // Extract actual File objects for uploader service
    const fileObjects = files.map((f) => (f as any).file || f) as File[]

    // Register upload with uploader service
    const uploadId = uploaderService.startUpload(fileObjects, abortController, {
      promise: uploadPromise
    })

    // Track progress if callback provided
    if (onProgress && uploadId !== undefined) {
      // Set initial progress
      uploaderService.setProgress(uploadId, 0)
    }

    // ETMF-specific upload endpoint
    const result = await uploadPromise

    // Mark as complete
    if (uploadId !== undefined) {
      uploaderService.setProgress(uploadId, 100)
    }

    return {
      success: true,
      data: result
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Filing upload failed'
    }
  }
}

// Legacy-compatible export object
// Provides the same API surface as the AngularJS service for gradual migration
export const filesMigration = {
  doMigration,
  doMigrationForFiling,
  performMigration,
  configureMigrationDetails,
  uploadFilesDirect,
  submitS3Upload,
  getAwsS3Config
}
